/**
 * x-mega-menu directive.
 *
 * Manages an accessible mega menu with hover and keyboard interaction. Only
 * one menu may be open at a time; opening a new one closes the previous one.
 * A diagonal-detection delay prevents premature closure when the mouse moves
 * between adjacent top-level items.
 *
 * Sub-directives:
 *   x-mega-menu:toggle    - Applied to the trigger button. Manages hover,
 *                           keyboard, and ARIA attributes.
 *   x-mega-menu:container - Applied to the dropdown container. Manages hover
 *                           cancellation, keyboard navigation, and `inert`.
 *
 * @options (root only)
 *   timeoutDuration    - Milliseconds to wait before closing on mouse-leave (default: 50).
 *   intentDelay        - Milliseconds to wait before opening when diagonal movement
 *                        is detected (default: 150).
 *   animationDuration  - GSAP fade animation duration in seconds (default: 0.2).
 *
 * @events dispatched
 *   mega-menu-opened  - Fired when the open animation completes.
 *   mega-menu-closing - Fired when closing begins, with `duration`.
 *   mega-menu-closed  - Fired when the menu is fully closed.
 *
 * @example
 *   <div x-mega-menu>
 *     <button x-mega-menu:toggle>Products</button>
 *     <div x-mega-menu:container>…</div>
 *   </div>
 */
(() => {
  /**
   * Singleton that tracks the latest known mouse cursor position.
   * Used for diagonal-movement detection between adjacent menu items.
   */
  const MouseTracker = {
    x: null,
    y: null,
    initialized: false,

    /**
     * Attaches a passive document mousemove listener to track cursor position.
     * No-ops if already initialised.
     */
    init() {
      if (this.initialized) return;
      document.addEventListener(
        "mousemove",
        (e) => {
          this.x = e.clientX;
          this.y = e.clientY;
        },
        { passive: true }
      );
      this.initialized = true;
    },

    /**
     * Returns the current cursor position.
     * @returns {{ x: number|null, y: number|null }}
     */
    getPosition() {
      return { x: this.x, y: this.y };
    },
  };

  /**
   * Singleton that tracks the currently open mega menu instance.
   */
  const MegaMenuState = {
    activeInstance: null,

    /**
     * Marks the given instance as the active (open) menu.
     * @param {object} instance - The Alpine data context of the active menu.
     */
    setActive(instance) {
      this.activeInstance = instance;
    },

    /**
     * Returns the currently active menu instance, or null if none is open.
     * @returns {object|null}
     */
    getActive() {
      return this.activeInstance;
    },

    /**
     * Clears the active instance only if it matches the provided one.
     * @param {object} instance - The instance to clear.
     */
    clearIfActive(instance) {
      if (this.activeInstance === instance) {
        this.activeInstance = null;
      }
    },
  };

  /**
   * Binds the nav link element with hover behaviours only.
   * Keyboard toggle is handled by the adjacent button (x-mega-menu:button).
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The nav link element.
   */
  const link = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["@mouseenter"]() {
        this.isHoveringToggle = true;
        this.handleToggleEnter();
      },
      ["@mouseleave"]() {
        this.isHoveringToggle = false;
        this.scheduleClose();
      },
      ["@keydown.escape"]() {
        if (this.open) this.closeMenuAndRestoreFocus();
      },
    }));
  };

  /**
   * Binds the adjacent toggle button with keyboard and ARIA behaviours.
   * Keyboard users Tab to this button to open/close the submenu without
   * intercepting the navigation link's Enter key.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The toggle button element.
   */
  const button = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "toggle",
      [":aria-expanded"]() {
        return this.open.toString();
      },
      ["@keydown.enter.prevent"]() {
        this.handleKeyboardToggle();
      },
      ["@keydown.space.prevent"]() {
        this.handleKeyboardToggle();
      },
      ["@keydown.escape"]() {
        if (this.open) this.closeMenuAndRestoreFocus();
      },
      ["@keydown.arrow-down.prevent"]() {
        if (!this.open) {
          this.openMenuWithFocus();
        } else {
          this.focusFirstMenuItem();
        }
      },
    }));
  };

  /**
   * Binds the dropdown container element with hover cancellation, keyboard
   * navigation (Arrow keys, Home, End), and the `inert` attribute.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The container element.
   */
  const container = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "container",
      [":inert"]() {
        return !this.open;
      },
      ["@mouseenter"]() {
        this.isHoveringContainer = true;
        this.cancelClose();
      },
      ["@mouseleave"]() {
        this.isHoveringContainer = false;
        this.scheduleClose();
      },
      ["@keydown.escape"]() {
        if (this.open) this.closeMenuAndRestoreFocus();
      },
      ["@keydown.arrow-down.prevent"]() {
        this.$focus.within(el).next();
      },
      ["@keydown.arrow-up.prevent"]() {
        this.$focus.within(el).previous();
      },
      ["@keydown.home.prevent"]() {
        this.$focus.within(el).first();
      },
      ["@keydown.end.prevent"]() {
        this.$focus.within(el).last();
      },
    }));
  };

  const root = (Alpine, el, options = {}) => {
    Alpine.bind(el, () => ({
      ["x-data"]() {
        let closeTimer = null;
        let intentTimer = null;
        let currentTween = null;

        /** Clears the scheduled-close timer. */
        const clearCloseTimer = () => {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
        };

        /** Clears the intent (open-delay) timer. */
        const clearIntentTimer = () => {
          if (intentTimer) {
            clearTimeout(intentTimer);
            intentTimer = null;
          }
        };

        /** Kills the active GSAP tween and nulls the reference. */
        const killTween = () => {
          if (currentTween) {
            currentTween.kill();
            currentTween = null;
          }
        };

        /**
         * Returns true when an intent delay should be added before opening
         * this menu. A delay is added when a different menu is currently open
         * and the cursor appears to be moving horizontally at the same level.
         * @param {object} context - The Alpine data context of this menu instance.
         * @returns {boolean}
         */
        const shouldAddDelay = (context) => {
          const activeInstance = MegaMenuState.getActive();
          if (!activeInstance || activeInstance === context) return false;

          const activeContainer = activeInstance.$refs.container;
          const newToggle = context.$refs.toggle;
          if (!activeContainer || !newToggle) return false;

          const { x: mouseX, y: mouseY } = MouseTracker.getPosition();
          if (mouseX === null || mouseY === null) return false;

          const activeRect = activeContainer.getBoundingClientRect();
          const newRect = newToggle.getBoundingClientRect();

          // Check if moving horizontally at the same level.
          const toggleCenter = (newRect.top + newRect.bottom) / 2;
          const isAtSameLevel =
            toggleCenter >= activeRect.top - 100 &&
            toggleCenter <= activeRect.bottom + 100;

          return isAtSameLevel;
        };

        return {
          open: false,
          isHoveringToggle: false,
          isHoveringContainer: false,

          timeoutDuration: options.timeoutDuration ?? 50,
          intentDelay: options.intentDelay ?? 150,
          animationDuration: options.animationDuration ?? 0.2,

          /**
           * Returns true when either the toggle or container is being hovered.
           * @returns {boolean}
           */
          get isHovering() {
            return this.isHoveringToggle || this.isHoveringContainer;
          },

          /**
           * Initialises the global mouse tracker.
           */
          init() {
            MouseTracker.init();
          },

          /**
           * Cleans up timers, kills any tween, and resets GSAP styles on teardown.
           */
          destroy() {
            clearCloseTimer();
            clearIntentTimer();
            killTween();
            this.open = false;
            MegaMenuState.clearIfActive(this);
            const containerEl = this.$refs.container;
            if (containerEl) {
              this.$gsap.set(containerEl, { clearProps: "all" });
            }
          },

          /**
           * Handles Enter/Space key presses on the toggle — opens with focus
           * or closes with focus restoration.
           */
          handleKeyboardToggle() {
            if (this.open) {
              this.closeMenuAndRestoreFocus();
            } else {
              this.openMenuWithFocus();
            }
          },

          /**
           * Opens the menu and moves focus to the first item once open.
           * @returns {Promise<void>}
           */
          async openMenuWithFocus() {
            await this.openMenu();
            if (this.open) {
              this.$nextTick(() => this.focusFirstMenuItem());
            }
          },

          /**
           * Closes the menu immediately without animation and returns focus
           * to the toggle button.
           */
          closeMenuAndRestoreFocus() {
            clearCloseTimer();
            clearIntentTimer();
            killTween();

            this.$dispatch("mega-menu-closing", {
              instance: this,
              duration: 0,
            });

            this.open = false;

            this.$dispatch("mega-menu-closed", {
              instance: this,
              duration: 0,
            });

            const containerEl = this.$refs.container;
            if (containerEl) {
              this.$gsap.set(containerEl, {
                display: "none",
                opacity: 0,
                zIndex: 10,
              });
            }

            MegaMenuState.clearIfActive(this);
            this.$nextTick(() => this.$refs.toggle?.focus());
          },

          /**
           * Moves focus to the first focusable item in the container.
           */
          focusFirstMenuItem() {
            const container = this.$refs.container;
            if (container) this.$focus.within(container).first();
          },

          /**
           * Cancels a pending close timer (called when the container is re-entered).
           */
          cancelClose() {
            clearCloseTimer();
          },

          /**
           * Handles the mouse entering the toggle. Opens the menu immediately
           * unless a diagonal-movement delay is required.
           * @returns {Promise<void>}
           */
          async handleToggleEnter() {
            clearIntentTimer();
            clearCloseTimer();

            // If already open, just keep it open.
            if (this.open) {
              return;
            }

            // Check if we should delay.
            if (shouldAddDelay(this)) {
              intentTimer = setTimeout(async () => {
                if (this.isHovering) {
                  await this.openMenu();
                }
              }, this.intentDelay);
            } else {
              await this.openMenu();
            }
          },

          /**
           * Opens this menu, closing any other currently open mega menu first.
           * Fades the container in with GSAP and dispatches `mega-menu-opened`.
           * @returns {Promise<void>}
           */
          async openMenu() {
            // If already open, do nothing.
            if (this.open) return;

            const containerEl = this.$refs.container;
            if (!containerEl) return;

            // Close other menu first.
            const activeInstance = MegaMenuState.getActive();
            if (activeInstance && activeInstance !== this) {
              await activeInstance.closeMenuFast();
            }

            // Open this menu.
            this.open = true;
            MegaMenuState.setActive(this);

            killTween();
            this.$gsap.killTweensOf(containerEl);

            this.$gsap.set(containerEl, {
              display: "block",
              opacity: 0,
              zIndex: 10,
              pointerEvents: "none",
            });

            currentTween = this.$gsap.to(containerEl, {
              duration: this.animationDuration,
              opacity: 1,
              ease: "power2.out",
              onComplete: () => {
                this.$gsap.set(containerEl, {
                  zIndex: 10,
                  pointerEvents: "auto",
                });

                currentTween = null;

                this.$dispatch("mega-menu-opened", { instance: this });
              },
            });
          },

          /**
           * Schedules the menu to close after `timeoutDuration` if the pointer
           * is no longer hovering when the timer fires.
           * @returns {Promise<void>}
           */
          scheduleClose() {
            if (this.isHovering) return;

            clearCloseTimer();

            closeTimer = setTimeout(async () => {
              if (!this.isHovering) {
                await this.closeMenu();
              }
            }, this.timeoutDuration);
          },

          /**
           * Closes the menu with the standard fade-out animation and dispatches
           * `mega-menu-closing` and `mega-menu-closed` events.
           * @returns {Promise<void>}
           */
          async closeMenu() {
            if (!this.open) return;

            const containerEl = this.$refs.container;
            if (!containerEl) return;

            killTween();
            this.$gsap.killTweensOf(containerEl);

            this.$gsap.set(containerEl, {
              pointerEvents: "none",
              zIndex: 1,
            });

            this.$dispatch("mega-menu-closing", {
              instance: this,
              duration: this.animationDuration,
            });

            currentTween = this.$gsap.to(containerEl, {
              duration: this.animationDuration,
              opacity: 0,
              ease: "power2.in",
              onComplete: () => {
                this.$gsap.set(containerEl, {
                  display: "none",
                  zIndex: 10,
                  pointerEvents: "auto",
                });

                this.open = false;
                MegaMenuState.clearIfActive(this);
                currentTween = null;

                this.$dispatch("mega-menu-closed", { instance: this });
              },
            });
          },

          /**
           * Closes the menu at half the normal animation speed. Used when
           * another menu needs to open immediately after this one.
           * @returns {Promise<void>}
           */
          async closeMenuFast() {
            if (!this.open) return;

            const containerEl = this.$refs.container;
            if (!containerEl) return;

            clearCloseTimer();
            clearIntentTimer();
            killTween();
            this.$gsap.killTweensOf(containerEl);

            this.$gsap.set(containerEl, {
              pointerEvents: "none",
              zIndex: 1,
            });

            this.$dispatch("mega-menu-closing", {
              instance: this,
              duration: this.animationDuration * 0.5,
            });

            await new Promise((resolve) => {
              currentTween = this.$gsap.to(containerEl, {
                duration: this.animationDuration * 0.5,
                opacity: 0,
                ease: "power2.in",
                onComplete: resolve,
              });
            });

            this.$gsap.set(containerEl, {
              display: "none",
              opacity: 0,
              zIndex: 10,
              pointerEvents: "auto",
            });

            this.open = false;
            MegaMenuState.clearIfActive(this);
            currentTween = null;

            this.$dispatch("mega-menu-closed", { instance: this });
          },

          /**
           * Forces the menu open immediately, bypassing timers and hover checks.
           * Used by the Shopify theme editor when a block is selected.
           */
          forceOpen() {
            // Clear all timers and reset hover states.
            clearCloseTimer();
            clearIntentTimer();

            // Set hover state to prevent immediate closure.
            this.isHoveringToggle = true;

            // Open the menu immediately.
            this.openMenu();
          },

          /**
           * Forces the menu closed immediately without animation.
           * Used by the Shopify theme editor when a block is deselected.
           */
          forceClose() {
            // Clear all timers.
            clearCloseTimer();
            clearIntentTimer();

            // Reset hover states.
            this.isHoveringToggle = false;
            this.isHoveringContainer = false;

            // Close immediately without animation.
            const containerEl = this.$refs.container;
            if (!containerEl) return;

            killTween();
            this.$gsap.killTweensOf(containerEl);

            this.$dispatch("mega-menu-closing", {
              instance: this,
              duration: 0,
            });

            this.$gsap.set(containerEl, {
              display: "none",
              opacity: 0,
              zIndex: 10,
              pointerEvents: "auto",
            });

            this.open = false;
            MegaMenuState.clearIfActive(this);

            this.$dispatch("mega-menu-closed", { instance: this });
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("mega-menu", (el, { expression, value }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};

      if ("link" === value) {
        link(Alpine, el);
      } else if ("button" === value) {
        button(Alpine, el);
      } else if ("container" === value) {
        container(Alpine, el);
      } else {
        root(Alpine, el, options);
      }
    });
  });
})();
